import { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import get from 'lodash/get';

import { Button, Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { LanguageCode } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import Article from '~/ui/components/layouts/Article';
import { generateLinkComponents, getRoutePostLink } from '~/utils/blog';

import { LP_ARTICLE_NAME, postData } from '~/constants/blog';

interface Section {
  id: number;
  url: string;
  alt: string;
  heading: any;
  text: any;
  orientation: 'left' | 'right';
}

const LandingPage = (): ReactElement => {
  const { t: tArticle } = useTranslation('', { keyPrefix: `$local.$articles.${LP_ARTICLE_NAME}` });

  const router = useRouter();
  const locale = router.locale as LanguageCode;

  const articleData = {
    id: 0,
    next: 1,
    hero: {
      img: get(postData, [0, 'heroImageUrl']),
      heading: tArticle('title'),
      text: tArticle('subTitle'),
    },
  };

  const sections: Section[] = [
    {
      id: 1,
      url: get(postData, [1, 'urls', locale]),
      alt: 'Mercure Store Blog 1',
      heading: tArticle('section1Heading'),
      text: tArticle('section1Text'),
      orientation: 'left',
    },
    {
      id: 2,
      url: get(postData, [2, 'urls', locale]),
      alt: 'Mercure Store Blog 2',
      heading: tArticle('section2Heading'),
      text: tArticle('section2Text'),
      orientation: 'right',
    },
    {
      id: 3,
      url: get(postData, [3, 'urls', locale]),
      heading: tArticle('section3Heading'),
      alt: 'Mercure Store Blog 3',
      text: tArticle('section3Text'),
      orientation: 'left',
    },
    {
      id: 4,
      url: get(postData, [4, 'urls', locale]),
      alt: 'Mercure Store Blog 4',
      heading: (
        <Trans
          i18nKey="section4Heading"
          t={tArticle}
          components={{
            sup: <sup />,
          }}
        />
      ),
      text: (
        <Trans
          i18nKey="section4Text"
          t={tArticle}
          components={{
            sup: <sup />,
            ...generateLinkComponents(tArticle, 1, 2),
          }}
        />
      ),
      orientation: 'right',
    },
  ];

  return (
    <Article id={articleData.id} hero={articleData.hero}>
      {sections.map(({ id, url, alt, heading, text, orientation }) => (
        <div
          key={id}
          className={`landing-section ${orientation === 'left' ? '' : 'landing-section-reverse'}`}
        >
          <img
            src={`/images/blog/hp-blog-mercure-article-${id}-content.webp`}
            alt={alt}
            className="landing-section-img"
          />
          <div className="landing-section-copy">
            <h3 className="landing-section-heading">{heading}</h3>
            <p className="landing-section-text">{text}</p>
            <Link
              href={getRoutePostLink(url, locale)}
              className="landing-section-btn"
              data-event-name="bloc_interact"
              data-bloc-name={heading}
            >
              <Button variant="plain">{tArticle('readMore')}</Button>
            </Link>
          </div>
        </div>
      ))}
    </Article>
  );
};

export default LandingPage;
